import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Modal,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import HistoryIcon from "@mui/icons-material/History";
import ImageIcon from "@mui/icons-material/Image";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ThemeContext } from "../ThemeContext";
import {
  addLetter,
  getOldLetters,
  uploadImage,
  clearSentLetters,
  clearProfileLimit,
  clearPhotoHash,
  clearAllData,
  startOnline,
  stopOnline,
  getProfileData,
  getBotStatus,
} from "../API";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!API_BASE_URL) {
    throw new Error("REACT_APP_API_BASE_URL не задан в .env");
}

const ProfileCard = ({
  id,
  profile,
  onClick,
  onLetterChange,
  editMode,
  onUpdate,
}) => {
  const { darkMode } = useContext(ThemeContext);

  // DnD kit
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : "auto",
    opacity: isDragging ? 0.8 : 1,
  };

  // --- ЛОГИКА ПИСЕМ ---
  const [savedLetter, setSavedLetter] = useState(profile.letter || "");
  const [currentLetter, setCurrentLetter] = useState(profile.letter || "");
  const [isLetterSaved, setIsLetterSaved] = useState(!!profile.letter && profile.letter.trim() !== "");
  const [isEdited, setIsEdited] = useState(false);
  const [oldLetters, setOldLetters] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [photoHash, setPhotoHash] = useState(profile.photo_hash || null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  // --- ИСТОЧНИКИ РАССЫЛКИ ---
  const availableSources = ["favorites", "my-favorites", "offline", "payers"];
  const [selectedSources, setSelectedSources] = useState([]);

  // --- СЧЁТЧИКИ ---
  const getCount = (field) => {
    if (!field) return 0;
    if (typeof field === "number") return field;
    if (Array.isArray(field)) return field.length;
    if (typeof field === "string")
      return field.split(",").filter((id) => id.trim() !== "").length;
    return 0;
  };

  const [sentLettersCount, setSentLettersCount] = useState(
    getCount(profile.sent_letters)
  );
  const [profileLimitCount, setProfileLimitCount] = useState(
    getCount(profile.profile_limit)
  );
  const [onlineCount, setOnlineCount] = useState(0);
  const [offlineCount, setOfflineCount] = useState(
    getCount(profile.offline_users || profile.offline)
  );
  const [ignoreCount, setIgnoreCount] = useState(getCount(profile.ignore));

  // --- СТАТУС БОТА (active / inactive) ---
  const [botStatus, setBotStatus] = useState("inactive");

  useEffect(() => {
    const fetchBotStatus = async () => {
      try {
        const resp = await getBotStatus(profile.id);
        setBotStatus(resp.status);
      } catch (error) {
        console.error("Ошибка при получении статуса бота:", error);
        setBotStatus("inactive");
      }
    };
    fetchBotStatus();
  }, [profile.id]);

  useEffect(() => {
    const fetchProfileDataFn = async () => {
      try {
        const response = await getProfileData(profile.id);
        const data = response.data;
        setSentLettersCount(
          typeof data.sent_letters === "number" ? data.sent_letters : 0
        );
        setProfileLimitCount(
          typeof data.profile_limit === "number" ? data.profile_limit : 0
        );
        setOfflineCount(
          typeof data.offline_users === "number" ? data.offline_users : 0
        );
        setIgnoreCount(typeof data.ignore === "number" ? data.ignore : 0);
        setOnlineCount(
          typeof data.online_users === "number" ? data.online_users : 0
        );
      } catch (error) {
        console.error("Ошибка при обновлении данных профиля:", error);
        setSnackbarMessage("Ошибка при обновлении данных профиля.");
        setSnackbarOpen(true);
      }
    };
    fetchProfileDataFn();
    const intervalId = setInterval(fetchProfileDataFn, 5000);
    return () => clearInterval(intervalId);
  }, [profile.id]);

  const handleStop = useCallback(async () => {
    try {
      await stopOnline(profile.id);
      setBotStatus("inactive");
      setSnackbarMessage("Профиль остановлен!");
      setSnackbarOpen(true);
      onUpdate && onUpdate();
    } catch (error) {
      console.error("Ошибка при остановке профиля:", error);
      setSnackbarMessage("Ошибка при остановке профиля.");
      setSnackbarOpen(true);
    }
  }, [profile.id, onUpdate]);

  const handleStart = useCallback(async () => {
    try {
      await startOnline(profile.id);
      setBotStatus("active");
      setSnackbarMessage("Профиль запущен в онлайн!");
      setSnackbarOpen(true);
      onUpdate && onUpdate();
    } catch (error) {
      console.error("Ошибка при запуске профиля:", error);
      setSnackbarMessage("Ошибка при запуске профиля.");
      setSnackbarOpen(true);
    }
  }, [profile.id, onUpdate]);

  useEffect(() => {
    if (sentLettersCount >= 500 && botStatus === "active") {
      handleStop();
    }
  }, [sentLettersCount, botStatus, handleStop]);

  const handleLetterChange = (e) => {
    const newText = e.target.value;
    setCurrentLetter(newText);
    setIsEdited(true);
    setIsLetterSaved(newText === savedLetter);
    onLetterChange(profile.id, newText);
  };

  const handleSave = async () => {
    try {
      if (!currentLetter.trim()) {
        setSnackbarMessage("Письмо не может быть пустым!");
        setSnackbarOpen(true);
        return;
      }
      await addLetter(profile.id, currentLetter);
      setSavedLetter(currentLetter);
      setIsEdited(false);
      setIsLetterSaved(true);
      setSnackbarMessage("Письмо успешно сохранено!");
      setSnackbarOpen(true);
      onUpdate && onUpdate();
    } catch (error) {
      console.error("Ошибка при сохранении письма:", error);
      setSnackbarMessage("Ошибка при сохранении письма.");
      setSnackbarOpen(true);
    }
  };

  const handleOpenModal = async () => {
    try {
      const response = await getOldLetters(profile.id);
      setOldLetters(response.data.old_letters);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Ошибка при получении старых писем:", error);
      setSnackbarMessage("Ошибка при загрузке старых писем.");
      setSnackbarOpen(true);
    }
  };
  const handleCloseModal = () => setIsModalOpen(false);

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreviewUrl(reader.result);
      reader.readAsDataURL(file);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("profile_id", profile.id);
      try {
        const response = await uploadImage(formData);
        if (response.data.photo_hash) {
          setPhotoHash(response.data.photo_hash);
          setImagePreviewUrl(null);
          if (response.data.duplicate) {
            setSnackbarMessage("Изображение уже существует. Запись обновлена.");
          } else {
            setSnackbarMessage("Изображение успешно загружено.");
          }
          setSnackbarOpen(true);
          onUpdate && onUpdate();
        } else {
          throw new Error("Не удалось получить хеш изображения.");
        }
      } catch (error) {
        console.error("Ошибка при загрузке изображения:", error);
        setSnackbarMessage("Ошибка при загрузке изображения.");
        setSnackbarOpen(true);
      }
    }
  };

  const handleDeleteImage = async () => {
    try {
      await clearPhotoHash(profile.id);
      setPhotoHash(null);
      setSnackbarMessage("Изображение успешно удалено!");
      setSnackbarOpen(true);
      onUpdate && onUpdate();
    } catch (error) {
      console.error("Ошибка при удалении изображения:", error);
      setSnackbarMessage("Ошибка при удалении изображения.");
      setSnackbarOpen(true);
    }
  };

  const handleClearSentLetters = async () => {
    try {
      await clearSentLetters(profile.id);
      setSentLettersCount(0);
      setSnackbarMessage("Отправленные письма успешно очищены!");
      setSnackbarOpen(true);
      onUpdate && onUpdate();
    } catch (error) {
      console.error("Ошибка при очистке отправленных писем:", error);
      setSnackbarMessage("Ошибка при очистке отправленных писем.");
      setSnackbarOpen(true);
    }
  };

  const handleClearProfileLimit = async () => {
    try {
      await clearProfileLimit(profile.id);
      setProfileLimitCount(0);
      setSnackbarMessage("Лимиты успешно очищены!");
      setSnackbarOpen(true);
      onUpdate && onUpdate();
    } catch (error) {
      console.error("Ошибка при очистке лимитов:", error);
      setSnackbarMessage("Ошибка при очистке лимитов.");
      setSnackbarOpen(true);
    }
  };

  const handleClearAllData = async () => {
    try {
      await clearAllData(profile.id);
      setSentLettersCount(0);
      setProfileLimitCount(0);
      setOnlineCount(0);
      setOfflineCount(0);
      setIgnoreCount(0);
      setSnackbarMessage("Все данные успешно очищены!");
      setSnackbarOpen(true);
      onUpdate && onUpdate();
    } catch (error) {
      console.error("Ошибка при очистке всех данных:", error);
      setSnackbarMessage("Ошибка при очистке всех данных.");
      setSnackbarOpen(true);
    }
  };

  const handleSourceChange = (event) => {
    const { name, checked } = event.target;
    if (checked) {
      setSelectedSources((prev) => [...prev, name]);
    } else {
      setSelectedSources((prev) =>
        prev.filter((source) => source !== name)
      );
    }
  };

  const getImageUrl = () => {
    if (imagePreviewUrl) {
      return imagePreviewUrl;
    } else if (photoHash) {
      return `${API_BASE_URL}/static/letter_photo/${profile.id}/${photoHash}.jpg`;
    } else {
      return `${API_BASE_URL}/static/images.jpeg`;
    }
  };

  const getAvatarUrl = () => {
    if (profile.avatar_url) {
      return profile.avatar_url;
    }
    return `${API_BASE_URL}/static/images.jpeg`;
  };

  const isBotActive = botStatus === "active";

  return (
    <>
      <Box
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={() => onClick(profile.id)}
        sx={{
          width: "300px",
          padding: "1rem",
          borderRadius: "15px",
          backgroundColor: darkMode ? "#161b22" : "#ffffff",
          boxShadow: darkMode
            ? "0 5px 20px rgba(0, 0, 0, 0.6)"
            : "0 5px 20px rgba(0, 0, 0, 0.1)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          overflow: "hidden",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          border: editMode ? "2px solid #3f51b5" : "none",
          transition: "box-shadow 0.2s ease, border 0.2s ease",
          position: "relative",
        }}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleOpenModal();
          }}
          sx={{
            position: "absolute",
            top: 8,
            right: 48,
            color: darkMode ? "#ffffff" : "#000000",
            zIndex: 10,
          }}
        >
          <HistoryIcon />
        </IconButton>

        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setIsSettingsOpen(true);
          }}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: darkMode ? "#ffffff" : "#000000",
            zIndex: 10,
          }}
        >
          <SettingsIcon />
        </IconButton>

        <Box
          sx={{
            width: "100%",
            height: "200px",
            borderRadius: "15px",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: darkMode ? "#0d1117" : "#f0f0f0",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              zIndex: 10,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Box
                sx={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: isBotActive ? "green" : "red",
                }}
              />
              <Typography variant="caption">Рассылка</Typography>
            </Box>
          </Box>

          <img
            src={getAvatarUrl()}
            alt="Avatar"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `${API_BASE_URL}/static/images.jpeg`;
            }}
          />
        </Box>

        <Box sx={{ width: "100%", padding: "1rem 0" }}>
          <Typography variant="h6" sx={{ textAlign: "center", fontWeight: "bold" }}>
            {profile.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography>Отправленные письма: {sentLettersCount}</Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClearSentLetters();
              }}
              sx={{ marginLeft: "auto" }}
              disabled={isBotActive}
            >
              <Tooltip title="Очистить отправленные письма">
                <DeleteIcon fontSize="small" />
              </Tooltip>
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography>Лимиты: {profileLimitCount}</Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClearProfileLimit();
              }}
              sx={{ marginLeft: "auto" }}
              disabled={isBotActive}
            >
              <Tooltip title="Очистить лимиты">
                <DeleteIcon fontSize="small" />
              </Tooltip>
            </IconButton>
          </Box>
          <Typography>Онлайн: {onlineCount}</Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography>Оффлайн: {offlineCount}</Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClearAllData();
              }}
              sx={{ marginLeft: "auto" }}
              disabled={isBotActive}
            >
              <Tooltip title="Очистить все данные">
                <DeleteIcon fontSize="small" />
              </Tooltip>
            </IconButton>
          </Box>
          <Typography>Игнор: {ignoreCount}</Typography>
        </Box>

        <Box sx={{ width: "100%", position: "relative" }}>
          <TextField
            placeholder="Введите текст..."
            multiline
            fullWidth
            minRows={2}
            value={currentLetter}
            onChange={handleLetterChange}
            disabled={isBotActive}
            sx={{
              marginBottom: "1rem",
              backgroundColor: darkMode ? "#161b22" : "#ffffff",
              borderRadius: "8px",
            }}
          />
          {isEdited && (
            <IconButton
              onClick={handleSave}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "green",
              }}
            >
              <CheckIcon />
            </IconButton>
          )}
        </Box>

        <Box sx={{ width: "100%", marginBottom: "1rem", marginTop: "auto" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              id={`upload-image-${profile.id}`}
              type="file"
              onChange={handleImageUpload}
              disabled={isBotActive}
            />
            <label htmlFor={`upload-image-${profile.id}`}>
              <Button
                variant="contained"
                component="span"
                startIcon={<ImageIcon />}
                fullWidth
                sx={{ marginBottom: "0.5rem" }}
                disabled={isBotActive}
              >
                Добавить фото
              </Button>
            </label>
            <IconButton
              color="error"
              onClick={handleDeleteImage}
              disabled={!photoHash || isBotActive}
            >
              <Tooltip title="Удалить фото">
                <DeleteIcon />
              </Tooltip>
            </IconButton>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: "100px",
              borderRadius: "8px",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: darkMode ? "#0d1117" : "#f0f0f0",
            }}
          >
            <img
              src={getImageUrl()}
              alt="Letter"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `${API_BASE_URL}/static/images.jpeg`;
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
          <Button
            variant="contained"
            color="success"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              handleStart();
            }}
            disabled={
              botStatus === "active" ||
              !currentLetter.trim() ||
              !isLetterSaved
            }
          >
            Старт
          </Button>
          <Button
            variant="contained"
            color="error"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              handleStop();
            }}
            disabled={botStatus === "inactive"}
          >
            Стоп
          </Button>
        </Box>
      </Box>

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="old-letters-modal-title"
        aria-describedby="old-letters-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: darkMode ? "#2c2c2c" : "#ffffff",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            borderRadius: "10px",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6">Старые письма</Typography>
          <List>
            {oldLetters.length > 0 ? (
              oldLetters.map((letter, index) => (
                <ListItem key={index} alignItems="flex-start">
                  <ListItemText
                    primary={`Письмо ${index + 1}`}
                    secondary={
                      typeof letter === "string"
                        ? letter
                        : JSON.stringify(letter)
                    }
                  />
                </ListItem>
              ))
            ) : (
              <Typography variant="body2">Нет старых писем.</Typography>
            )}
          </List>
          <Button onClick={handleCloseModal} variant="contained" sx={{ mt: 2 }}>
            Закрыть
          </Button>
        </Box>
      </Modal>

      <Modal
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        aria-labelledby="settings-modal-title"
        aria-describedby="settings-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 300,
            bgcolor: darkMode ? "#2c2c2c" : "#ffffff",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            borderRadius: "10px",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Настройки рассылки
          </Typography>
          <FormGroup>
            {availableSources.map((source) => (
              <FormControlLabel
                key={source}
                control={
                  <Checkbox
                    checked={selectedSources.includes(source)}
                    onChange={handleSourceChange}
                    name={source}
                  />
                }
                label={source.charAt(0).toUpperCase() + source.slice(1)}
              />
            ))}
          </FormGroup>
          <Button
            onClick={() => setIsSettingsOpen(false)}
            variant="contained"
            sx={{ mt: 2 }}
            fullWidth
          >
            Сохранить
          </Button>
        </Box>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
      />
    </>
  );
};

export default React.memo(ProfileCard);
